import {
  Autocomplete as NextUAutocomplete,
  AutocompleteProps as NextUIAutocompleteProps,
} from '@nextui-org/react';
import { Key } from '@react-types/shared';
import { ForwardedRef, forwardRef, useCallback } from 'react';

interface AutocompleteProps extends NextUIAutocompleteProps {
  query?: string;
}

export const Autocomplete = forwardRef(
  (props: AutocompleteProps, ref: ForwardedRef<HTMLInputElement>) => {
    const { query, onSelectionChange, onInputChange, classNames, inputProps, ...restProps } = props;

    // Handle selection change to prevent query from overriding selection
    const handleSelectionChange = useCallback(
      (key: Key | null) => {
        // Always call the original selection handler first
        if (onSelectionChange) {
          onSelectionChange(key);
        }
      },
      [onSelectionChange],
    );

    // Handle input change
    const handleInputChange = useCallback(
      (value: string) => {
        if (onInputChange) {
          onInputChange(value);
        }
      },
      [onInputChange],
    );

    return (
      <NextUAutocomplete
        ref={ref}
        inputValue={query}
        onSelectionChange={handleSelectionChange}
        onInputChange={handleInputChange}
        classNames={{
          clearButton: 'text-default-700 hover:text-default-900',
          selectorButton: 'text-default-700 hover:text-default-900',
          ...(classNames || {}),
        }}
        inputProps={{
          classNames: {
            inputWrapper: [
              'bg-white',
              'shadow-none',
              'border-2',
              restProps.isDisabled ? 'border-default' : 'border-primary',
            ],
            input: ['!text-foreground', '!placeholder:text-default-600', '!placeholder:opacity-80'],
            ...(inputProps?.classNames || {}),
          },
          ...(inputProps || {}),
        }}
        listboxProps={{
          autoFocus: false,
        }}
        {...restProps}
      >
        {props.children}
      </NextUAutocomplete>
    );
  },
);

Autocomplete.displayName = 'Autocomplete';

export { AutocompleteItem, AutocompleteSection } from '@nextui-org/react';
